import { useState } from "react";
import { Radar, ExternalLink, Send, Copy, Check, ShieldCheck, Sparkles, Tag } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Card, CardLabel } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { marketplaceLabel } from "@/lib/marketplace";
import { cn } from "@/lib/utils";
import type { Link as LinkType } from "@/lib/types";

interface DealRadarProps {
  readonly links: LinkType[];
  readonly isLoading?: boolean;
  readonly className?: string;
}

const PLATFORM_BADGES: Record<string, { bg: string; text: string; border: string }> = {
  amazon: { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/20" },
  mercadolivre: { bg: "bg-sky-500/10", text: "text-sky-400", border: "border-sky-500/20" },
  shopee: { bg: "bg-rose-500/10", text: "text-rose-400", border: "border-rose-500/20" },
  magalu: { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/20" },
  aliexpress: { bg: "bg-purple-500/10", text: "text-purple-400", border: "border-purple-500/20" },
  shein: { bg: "bg-pink-500/10", text: "text-pink-400", border: "border-pink-500/20" },
  awin: { bg: "bg-slate-500/10", text: "text-slate-300", border: "border-slate-500/20" },
};

export function DealRadar({ links, isLoading, className }: DealRadarProps): JSX.Element {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Filtra produtos rastreados com identificador de plataforma (ou os links mais recentes)
  const trackedLinks = links.filter((l) => Boolean(l.platform_item_id));
  const displayLinks = trackedLinks.length > 0 ? trackedLinks.slice(0, 6) : links.slice(0, 4);

  const handleCopy = (id: string, url: string): void => {
    void navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <Card variant="raised" className={cn("p-5 flex flex-col gap-4 border-[hsl(var(--border-strong))]", className)}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[hsl(var(--border))] pb-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            <Radar size={16} className="animate-pulse" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <CardLabel className="text-foreground font-semibold">Radar de Produtos Rastreados</CardLabel>
              <span className="flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-400">
                <ShieldCheck size={11} /> Anti-Ban Ativo
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Identificação automática de catálogo por ID de produto em 7 marketplaces.
            </p>
          </div>
        </div>

        <Button asChild variant="default" size="sm" className="h-8 gap-1.5 text-xs font-semibold">
          <Link to="/app/criar-posts">
            <Send size={12} />
            <span>Disparar Oferta</span>
          </Link>
        </Button>
      </div>

      {isLoading ? (
        <div className="animate-pulse space-y-3 py-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-14 rounded-lg bg-[hsl(var(--border-strong)/0.3)]" />
          ))}
        </div>
      ) : displayLinks.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {displayLinks.map((link) => {
            const badge = PLATFORM_BADGES[link.marketplace_code.toLowerCase()] ?? {
              bg: "bg-slate-500/10",
              text: "text-muted-foreground",
              border: "border-slate-500/20",
            };
            const isCopied = copiedId === link.id;

            return (
              <div
                key={link.id}
                className="flex flex-col justify-between gap-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-raised)/0.4)] p-3.5 hover:border-[hsl(var(--border-strong))] transition-all"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        "rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider",
                        badge.bg,
                        badge.text,
                        badge.border,
                      )}
                    >
                      {marketplaceLabel(link.marketplace_code)}
                    </span>
                    {link.platform_item_id && (
                      <span className="flex items-center gap-1 rounded bg-[hsl(var(--surface))] px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground border border-[hsl(var(--border))]">
                        <Tag size={10} />
                        ID: {link.platform_item_id}
                      </span>
                    )}
                  </div>

                  <a
                    href={link.destination_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground transition-colors p-1"
                    title="Abrir oferta"
                  >
                    <ExternalLink size={13} />
                  </a>
                </div>

                <p className="text-xs text-foreground font-medium truncate" title={link.destination_url}>
                  {link.destination_url}
                </p>

                <div className="flex items-center justify-between gap-2 pt-1 border-t border-[hsl(var(--border)/0.6)]">
                  <span className="text-[11px] text-muted-foreground">
                    Cliques: <strong className="text-foreground">{link.click_count ?? 0}</strong>
                  </span>

                  <div className="flex items-center gap-1.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopy(link.id, link.destination_url)}
                      className="h-6 px-2 text-[11px] gap-1 text-muted-foreground hover:text-foreground"
                    >
                      {isCopied ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
                      {isCopied ? "Copiado" : "Copiar"}
                    </Button>

                    <Button asChild variant="outline" size="sm" className="h-6 px-2 text-[11px] gap-1">
                      <Link to="/app/criar-posts">
                        <Sparkles size={11} className="text-amber-400" />
                        Criar Post
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-2 py-8 text-center border border-dashed border-[hsl(var(--border))] rounded-lg">
          <Radar size={28} className="text-muted-foreground/60" />
          <p className="text-sm font-medium text-foreground">Nenhum produto rastreado ainda</p>
          <p className="text-xs text-muted-foreground max-w-sm">
            Assim que você ou seu bot converter links de produtos da Shopee, Amazon, ML, AliExpress, Magalu ou Shein,
            eles aparecerão aqui no radar com proteção anti-ban e atalhos rápidos de republicação.
          </p>
        </div>
      )}
    </Card>
  );
}
